import React, { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import LeagueCandidateRow from "./LeagueCandidateRow";

export default function LeagueCandidatesIndex({
  candidates,
  selectedId,
  onSelect,
  pagination,
  loading,
  onPageChange,
}) {
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  const { page = 1, totalPages = 1, total = 0, limit = 25 } = pagination || {};
  const startIndex = (page - 1) * limit;
  const endIndex = Math.min(startIndex + candidates.length, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [page]);

  useEffect(() => {
    if (selectedRef.current && listRef.current) {
      const r = selectedRef.current.getBoundingClientRect();
      const c = listRef.current.getBoundingClientRect();
      if (r.top < c.top || r.bottom > c.bottom) {
        selectedRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [selectedId]);

  const goPage = (nextPage) => {
    if (loading || nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    onPageChange(nextPage);
  };

  return (
    <aside className="amf-index alg-index" aria-label="Candidate leagues">
      <div className="amf-index-head" aria-live="polite">
        <div className="amf-index-head-row">
          <span className="amf-index-brand-label">Candidate pool</span>
          <span className="amf-index-count">
            {total > 0 ? (
              <>
                <strong>
                  {startIndex + 1}–{endIndex}
                </strong>
                {" / "}
                <strong>{total}</strong>
              </>
            ) : (
              <strong>{candidates.length}</strong>
            )}
            {totalPages > 1 ? ` · p${page}` : ""}
          </span>
        </div>
      </div>

      <div className="amf-index-list" ref={listRef}>
        {loading && candidates.length === 0 ? (
          <div className="alg-index-loading">
            <Loader2 size={18} className="am-spin" aria-hidden />
            <span>Loading candidates…</span>
          </div>
        ) : candidates.length === 0 ? (
          <div className="amf-index-empty">
            No candidates yet. Use Discover to pull up to 20 active leagues from API-Sports.
          </div>
        ) : (
          candidates.map((candidate) => (
            <div
              key={candidate._id}
              ref={selectedId === candidate._id ? selectedRef : null}
            >
              <LeagueCandidateRow
                candidate={candidate}
                selected={selectedId === candidate._id}
                onSelect={() => onSelect(candidate._id)}
              />
            </div>
          ))
        )}
      </div>

      {totalPages > 1 ? (
        <div className="amf-index-foot">
          <button
            type="button"
            className="amf-index-page"
            disabled={!canPrev || loading}
            onClick={() => goPage(page - 1)}
          >
            <ChevronLeft size={14} />
          </button>
          <span className="amf-index-page-label">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            className="amf-index-page"
            disabled={!canNext || loading}
            onClick={() => goPage(page + 1)}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      ) : null}
    </aside>
  );
}
